import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { IztExamQuestion } from '../../api/types';
import { ChevronLeftIcon, ChevronRightIcon } from '../../layout/icons';
import { useOwnPager } from '../../stores/board';
import { observeElementSize } from '../../whiteboard/renderer';
import { onFullscreenChange } from '../link/fullscreen';
import { sanitizeExamHtml } from '../quiz/examHtml';
import { formatScore, sheetTally, studentSheet, type AnswerVerdict, type ExamAttempt, type SheetRow } from './examResults';
import { clampPage, pageOfRow, pageRowRange, sheetPageStarts, type RowSpan } from './sheetPages';

const PAGE_KEYS: Record<string, number | undefined> = { ArrowLeft: -1, PageUp: -1, ArrowRight: 1, PageDown: 1 };

/** A key typed into a field is the field's, never a page turn. */
function typingTarget(target: EventTarget | null): boolean {
  const node = target as HTMLElement | null;
  if (!node || !node.tagName) return false;
  return node.tagName === 'INPUT' || node.tagName === 'TEXTAREA' || node.tagName === 'SELECT' || node.isContentEditable;
}

const VERDICT_LABEL: Record<AnswerVerdict, string> = { correct: 'Đúng', wrong: 'Sai', blank: 'Bỏ trống' };

/** IZ exam html, sanitized, formulas and figures intact. */
export function ExamHtml({ html, className }: { html: string; className?: string }) {
  const clean = useMemo(() => sanitizeExamHtml(html), [html]);
  return <span className={className ? 'exam-html ' + className : 'exam-html'} dangerouslySetInnerHTML={{ __html: clean }} />;
}

function SheetQuestion({ row }: { row: SheetRow }) {
  return (
    <li className="exam-sheet__row">
      <div className="exam-sheet__head">
        <span className="exam-sheet__no">Câu {row.index + 1}</span>
        <span className={`exam-sheet__verdict exam-sheet__verdict--${row.verdict}`}>{VERDICT_LABEL[row.verdict]}</span>
      </div>
      <div className="exam-sheet__stem">{row.stemHtml ? <ExamHtml html={row.stemHtml} /> : row.hint || 'Câu hỏi không có nội dung xem trước.'}</div>
      {row.options.length > 0 ? (
        <ul className="exam-sheet__options">
          {row.options.map((option) => {
            const tone = option.correct ? ' exam-sheet__option--correct' : option.picked ? ' exam-sheet__option--wrong' : '';
            return (
              <li key={option.key} className={'exam-sheet__option' + tone}>
                <span className="exam-sheet__label">{option.label}</span>
                <ExamHtml html={option.html} />
                {option.picked ? <span className={option.correct ? 'exam-sheet__mark exam-sheet__mark--correct' : 'exam-sheet__mark exam-sheet__mark--wrong'}>Em chọn</span>
                  : option.correct ? <span className="exam-sheet__mark exam-sheet__mark--correct">Đáp án đúng</span> : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="exam-sheet__typed">
          <div><strong>Em trả lời:</strong>{row.typed || '(bỏ trống)'}</div>
          {row.correctText ? <div><strong>Đáp án đúng:</strong><ExamHtml html={row.correctText} /></div> : null}
        </div>
      )}
    </li>
  );
}

/** Pages the sheet to the free height, one page at a time, and hands the board's Trang pager to it. */
function useSheetPages(rowCount: number, resetKey: string) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLOListElement | null>(null);
  const sizeRef = useRef({ width: 0, height: 0 });
  const [starts, setStarts] = useState<number[]>([0]);
  // every row laid out for one pass, so each can be measured
  const [measuring, setMeasuring] = useState(true);
  const [page, setPage] = useState(0);
  // first row on screen, so a re-page (fullscreen, resize) stays on the same question
  const anchorRef = useRef(0);

  // another student starts on the first page
  useLayoutEffect(() => {
    setPage(0);
    anchorRef.current = 0;
    setMeasuring(true);
  }, [resetKey, rowCount]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const list = listRef.current;
    if (!measuring || !viewport || !list) return;
    sizeRef.current = { width: viewport.clientWidth, height: viewport.clientHeight };
    const rows: RowSpan[] = [];
    for (let i = 0; i < list.children.length; i += 1) {
      const row = list.children[i] as HTMLElement;
      rows.push({ top: row.offsetTop, bottom: row.offsetTop + row.offsetHeight });
    }
    const next = sheetPageStarts(rows, viewport.clientHeight);
    setStarts(next);
    setPage(pageOfRow(next, anchorRef.current));
    setMeasuring(false);
  }, [measuring, resetKey, rowCount]);

  const hasRows = rowCount > 0;
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!hasRows || !viewport) return undefined;
    const resized = () => {
      if (viewport.clientWidth !== sizeRef.current.width || viewport.clientHeight !== sizeRef.current.height) setMeasuring(true);
    };
    // a formula or figure that lands late changes its row's height
    const loaded = (event: Event) => { if ((event.target as HTMLElement).tagName === 'IMG') setMeasuring(true); };
    const stop = observeElementSize(viewport, resized);
    // Chrome 58 has no ResizeObserver, and entering fullscreen need not fire a window resize
    let settle: number | undefined;
    const stopFullscreen = onFullscreenChange(() => {
      resized();
      window.clearTimeout(settle);
      settle = window.setTimeout(resized, 300);
    });
    viewport.addEventListener('load', loaded, true);
    return () => {
      stop();
      stopFullscreen();
      window.clearTimeout(settle);
      viewport.removeEventListener('load', loaded, true);
    };
  }, [hasRows]);

  const shown = clampPage(page, starts.length);
  useLayoutEffect(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = 0;
  }, [shown]);

  const pager = useMemo(() => ({ index: shown, count: starts.length, go: (next: number) => setPage(clampPage(next, starts.length)) }), [shown, starts]);
  useOwnPager(hasRows ? pager : null);
  const pagerRef = useRef(pager);
  pagerRef.current = pager;

  // the keyboard steps pages too, also in fullscreen where the board's pager is out of reach
  useEffect(() => {
    if (!hasRows) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const delta = PAGE_KEYS[event.key];
      if (!delta || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || typingTarget(event.target)) return;
      event.preventDefault();
      pagerRef.current.go(pagerRef.current.index + delta);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hasRows]);

  const range = measuring ? { from: 0, to: rowCount } : pageRowRange(starts, rowCount, shown);
  if (!measuring) anchorRef.current = range.from;
  return { viewportRef, listRef, range, pager };
}

export interface ExamStudentSheetProps {
  /** Submitted students in Xếp hạng order; only they are navigable. */
  order: ExamAttempt[];
  index: number;
  names: Record<string, string>;
  questions: IztExamQuestion[];
  onStep: (index: number) => void;
  onBack: () => void;
}

// One student's graded attempt, a board page of questions at a time, with prev/next across the class
export function ExamStudentSheet({ order, index, names, questions, onStep, onBack }: ExamStudentSheetProps) {
  const attempt = order[index];
  const rows = useMemo(() => (attempt ? studentSheet(attempt, questions) : []), [attempt, questions]);
  const pages = useSheetPages(rows.length, attempt ? attempt.id : '');
  if (!attempt) return null;
  const tally = sheetTally(rows);
  const meta = [attempt.score !== null ? `${formatScore(attempt.score)} điểm` : '', `${tally.correct} đúng · ${tally.wrong} sai · ${tally.blank} bỏ trống`].filter(Boolean).join(' · ');
  return (
    <div className="exam-sheet">
      {/* its own pager: in fullscreen the sheet covers the board's toolbar */}
      <div className="exam-sheet__top">
        <button type="button" className="hw-panel__refresh exam-sheet__back" onClick={onBack}>Quay lại đề</button>
        {pages.pager.count > 1 ? (
          <div className="exam-sheet__pager" role="group" aria-label="Trang bài làm">
            <button type="button" className="exam-sheet__page-step" aria-label="Trang bài làm trước" disabled={pages.pager.index <= 0} onClick={() => pages.pager.go(pages.pager.index - 1)}><ChevronLeftIcon size={20} /></button>
            <span className="exam-sheet__page-label" aria-live="polite">Trang {pages.pager.index + 1}/{pages.pager.count}</span>
            <button type="button" className="exam-sheet__page-step" aria-label="Trang bài làm sau" disabled={pages.pager.index >= pages.pager.count - 1} onClick={() => pages.pager.go(pages.pager.index + 1)}><ChevronRightIcon size={20} /></button>
          </div>
        ) : null}
      </div>
      <div className="exam-sheet__nav" role="group" aria-label="Bài làm của học sinh">
        <button type="button" className="exam-sheet__step" aria-label="Học sinh trước" disabled={index <= 0} onClick={() => onStep(index - 1)}><ChevronLeftIcon size={22} /></button>
        <div className="exam-sheet__who" aria-live="polite">
          <strong title={names[attempt.code] || attempt.name}>{names[attempt.code] || attempt.name}</strong>
          <span>Em {index + 1}/{order.length} · {meta}</span>
        </div>
        <button type="button" className="exam-sheet__step" aria-label="Học sinh tiếp theo" disabled={index >= order.length - 1} onClick={() => onStep(index + 1)}><ChevronRightIcon size={22} /></button>
      </div>
      {rows.length === 0 ? <p className="quiz-results__empty">Bài làm này không có câu nào.</p> : (
        <div className="exam-sheet__pages" ref={pages.viewportRef}>
          <ol className="exam-sheet__list" ref={pages.listRef}>{rows.slice(pages.range.from, pages.range.to).map((row) => <SheetQuestion key={row.id} row={row} />)}</ol>
        </div>
      )}
    </div>
  );
}
